import React, { useEffect, useState, useRef } from 'react';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-fresh.css';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { array, bool, func, object } from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import {
   createDragToSelectHandlers,
   createFirstDataRenderedHandler,
   createRowClickHandler,
   handleKeyboardNavigation,
   navigateToNextCell,
} from './__helpers__/functions';

LicenseManager.setLicenseKey(process.env.AG_GRID_ENTERPRISE_LICENSE_KEY);
console.log('Ag grid key: ', process.env.AG_GRID_ENTERPRISE_LICENSE_KEY);
console.log(
   'GCP_REPOSITORY_SABRE_COM_USERNAME',
   process.env.GCP_REPOSITORY_SABRE_COM_USERNAME
);
console.log(
   'GCP_PROMOTE_REPOSITORY_SABRE_COM_USERNAME',
   process.env.GCP_PROMOTE_REPOSITORY_SABRE_COM_USERNAME
);
console.log('ARMADA_API_USER_TOKEN', process.env.ARMADA_API_USER_TOKEN);

const AgGridReactTable = ({
   rowData: rowDataFromParent,
   headers,
   onSelectedRowsChange,
   enableMultiRowSelection = false,
   resetTableOnRerender = false,
   autoSizeColumns = true,
   enableAllColumnResizable = true,
   enableAllColumnSort = true,
   enableAllColumnFilter = true,
   selectFirstRowByDefault = true,
   rowClassRules = {},
   enableRowIds = false,
   rememberSelectedRow = false,
}) => {
   const [rowData, setRowData] = useState([]);
   const [selectedRow, setSelectedRow] = useState(null);
   const gridApiRef = useRef(null);
   const isDraggingRef = useRef(false);
   const startRowRef = useRef(null);
   const lastRowClickedRef = useRef(null);
   const hostCarrierCode = sessionStorage.getItem('carrierCode') ?? null;

   const onGridReady = (params) => {
      gridApiRef.current = params.api;
   };

   const updateSelectedRows = () => {
      if (!gridApiRef?.current) return;
      const selectedNodes = gridApiRef?.current?.getSelectedNodes();
      onSelectedRowsChange?.(selectedNodes);
   };

   const { onCellMouseDown, onCellMouseOver } = createDragToSelectHandlers({
      isDraggingRef,
      startRowRef,
      updateSelectedRows,
      gridApiRef: gridApiRef.current,
   });

   // eslint-disable-next-line testing-library/render-result-naming-convention
   const onFirstDataRendered = createFirstDataRenderedHandler(autoSizeColumns);

   const handleRowClick = createRowClickHandler(
      updateSelectedRows,
      lastRowClickedRef
   );

   const onCellKeyDown = handleKeyboardNavigation(updateSelectedRows);

   useEffect(() => {
      setTimeout(() => {
         setRowData(rowDataFromParent);
      }, 0);
   }, [rowDataFromParent]);

   useEffect(() => {
      if (selectFirstRowByDefault) {
         if (rememberSelectedRow && selectedRow) return;

         const firstRowNode = gridApiRef?.current?.getDisplayedRowAtIndex(0);
         firstRowNode?.setSelected(true);
         setSelectedRow(firstRowNode);
         lastRowClickedRef.current = firstRowNode?.rowIndex;
      }
   }, [rowData, selectFirstRowByDefault, rememberSelectedRow, selectedRow]);

   const gridOptions = {
      suppressScrollOnNewData: true,
      suppressColumnVirtualisation: true,
      suppressRowClickSelection: true,
      undoRedoCellEditing: true,
      rowSelection: enableMultiRowSelection ? 'multiple' : 'single',
      onGridReady,
      onRowClicked: handleRowClick,
      tooltipShowDelay: 0,
      defaultColDef: {
         resizable: enableAllColumnResizable,
         filter: enableAllColumnFilter,
         sortable: enableAllColumnSort,
         unSortIcon: enableAllColumnSort,
      },
      onFirstDataRendered,
      rowClassRules: {
         'ag-row-white host-carrier': (params) =>
            params.data.carrier_code === hostCarrierCode,
         ...rowClassRules,
      },
      onCellKeyDown,
      onCellMouseDown,
      onCellMouseOver,
      navigateToNextCell,
      ...(enableRowIds && { getRowId: (params) => params.data.id }),
      ...(resetTableOnRerender && { key: uuidv4() }),
   };

   return (
      <section className='ag-grid-react-table ag-theme-fresh'>
         <AgGridReact rowData={rowData} columnDefs={headers} {...gridOptions} />
      </section>
   );
};

AgGridReactTable.propTypes = {
   rowData: array,
   headers: array,
   onSelectedRowsChange: func,
   enableUniqueRows: bool,
   enableMultiRowSelection: bool,
   resetTableOnRerender: bool,
   autoSizeColumns: bool,
   enableAllColumnResizable: bool,
   enableAllColumnSort: bool,
   enableAllColumnFilter: bool,
   selectFirstRowByDefault: bool,
   rowClassRules: object,
   enableRowIds: bool,
   rememberSelectedRow: bool,
};

export default AgGridReactTable;
